import { isPrivateOrReservedIp } from '@/shared/format/ip-address'
import { regionName } from '@/shared/format/region-name'

import type { GeoEntry, LogGeo } from '../types'

export type GeoSide = 'source' | 'destination'

/** One address on a side, with its lookup when the database had one. */
export type GeoAddressRow = { ip: string; entry?: GeoEntry }

const text = (value: unknown): string | undefined =>
	typeof value === 'string' && value !== '' ? value : undefined

const parseLocation = (value: unknown): GeoEntry['location'] => {
	if (!value || typeof value !== 'object') return undefined
	const { lat, lon } = value as Record<string, unknown>
	return typeof lat === 'number' && typeof lon === 'number'
		? { lat, lon }
		: undefined
}

/** Keeps an entry only if it is an object with a string `ip`; wrong-typed leaves are dropped. */
const parseEntry = (raw: unknown): GeoEntry | undefined => {
	if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined
	const r = raw as Record<string, unknown>
	const ip = text(r.ip)
	if (!ip) return undefined
	const entry: GeoEntry = { ip }
	const country_iso_code = text(r.country_iso_code)
	const country_name = text(r.country_name)
	const region_iso_code = text(r.region_iso_code)
	const region_name = text(r.region_name)
	const city_name = text(r.city_name)
	const location = parseLocation(r.location)
	if (country_iso_code) entry.country_iso_code = country_iso_code
	if (country_name) entry.country_name = country_name
	if (region_iso_code) entry.region_iso_code = region_iso_code
	if (region_name) entry.region_name = region_name
	if (city_name) entry.city_name = city_name
	if (location) entry.location = location
	return entry
}

const parseList = <T>(
	raw: unknown,
	parse: (item: unknown) => T | undefined,
): T[] | undefined => {
	if (!Array.isArray(raw)) return undefined
	const items = raw.map(parse).filter((item): item is T => item !== undefined)
	return items.length ? items : undefined
}

/**
 * Defensive parse of a log's `normalized_fields` (wire data, old logs may lack it
 * or any key in it). Returns `undefined` unless at least one geo entry survives on
 * either side, so a caller renders the section or nothing — never an empty shell.
 */
export const readLogGeo = (normalizedFields: unknown): LogGeo | undefined => {
	if (!normalizedFields || typeof normalizedFields !== 'object')
		return undefined
	const n = normalizedFields as Record<string, unknown>
	const geo: LogGeo = {}
	const source_geo = parseList(n.source_geo, parseEntry)
	const destination_geo = parseList(n.destination_geo, parseEntry)
	const source_ip = parseList(n.source_ip, text)
	const destination_ip = parseList(n.destination_ip, text)
	if (source_geo) geo.source_geo = source_geo
	if (destination_geo) geo.destination_geo = destination_geo
	if (source_ip) geo.source_ip = source_ip
	if (destination_ip) geo.destination_ip = destination_ip
	return source_geo || destination_geo ? geo : undefined
}

/**
 * "City, Region, Country" from whichever parts exist. The country falls back to
 * the English name of its ISO code. `undefined` when there is nothing to say.
 * Takes unchecked leaves on purpose: a dashboard cell is a raw BQL row, not a
 * parsed `GeoEntry`, and each leaf is read through `text` anyway.
 */
export const geoPlace = (
	entry: Partial<Record<keyof GeoEntry, unknown>>,
): string | undefined => {
	const code = text(entry.country_iso_code)
	const parts = [
		text(entry.city_name),
		text(entry.region_name),
		text(entry.country_name) ?? (code && regionName(code)),
	].filter((part): part is string => !!part)
	return parts.length ? parts.join(', ') : undefined
}

/**
 * Why addresses have no location: every one is private or reserved (GeoIP has
 * nothing for those), or a public one missed the lookup. `undefined` for none.
 */
export const noGeoReason = (
	addresses: readonly string[],
): string | undefined => {
	if (addresses.length === 0) return undefined
	return addresses.every(isPrivateOrReservedIp)
		? 'Private or reserved IP'
		: 'Unknown location'
}

/** Only the two geo lists, keys present only when set — what the Search JSON tab shows. */
export const geoJson = (
	geo: LogGeo,
): { source_geo?: GeoEntry[]; destination_geo?: GeoEntry[] } => {
	const out: { source_geo?: GeoEntry[]; destination_geo?: GeoEntry[] } = {}
	if (geo.source_geo) out.source_geo = geo.source_geo
	if (geo.destination_geo) out.destination_geo = geo.destination_geo
	return out
}

/**
 * The addresses of one side, in display order: the `*_ip` list as logged, then any
 * geo entry whose `ip` that list lacks. Each carries its lookup when it has one.
 */
export const geoAddressRows = (geo: LogGeo, side: GeoSide): GeoAddressRow[] => {
	const entries =
		(side === 'source' ? geo.source_geo : geo.destination_geo) ?? []
	const listed = (side === 'source' ? geo.source_ip : geo.destination_ip) ?? []
	const byIp = new Map(entries.map((entry) => [entry.ip, entry]))
	const ips = [...new Set([...listed, ...entries.map((entry) => entry.ip)])]
	return ips.map((ip) => ({ ip, entry: byIp.get(ip) }))
}
